import React, { ReactElement, useCallback, useMemo, useLayoutEffect } from "react";
import { useParams, useLocation } from "wouter";
import * as Tabs from "@radix-ui/react-tabs";
import { cloneDeep } from "lodash";
import { PartyBaseTab } from "./tabs/PartyBaseTab";
import { PartyAccountsTab } from "./tabs/PartyAccountsTab";
import { PartyExternalAccountsTab } from "./tabs/PartyExternalAccountsTab";
import { PartyInstrumentsTab } from "./tabs/PartyInstrumentsTab";
import { PartyFundInfoTab } from "./tabs/PartyFundInfoTab";
import { useQuery } from "urql";
import { GET_PARTY } from "../../../../graphql/src/party/queries";
import { Instrument, IssuerProgram, Party, PartyType } from "../../types.generated";

export type PartyPageData = {
    party?: Partial<Party>;
    clients?: Partial<Party>[];
    parties?: Partial<Party>[];
    instruments?: Partial<Instrument>[];
    instrumentsUsedBy?: Partial<Instrument>[];
    custodians?: Partial<Party>[];
    transferAgents?: Partial<Party>[];
    creditRatingAgencies?: Partial<Party>[];
    placesOfSettlement?: Partial<Party>[];
    fundInternals?: Partial<Instrument>[];
    issuerInstruments?: Partial<Instrument>[];
    issuerPrograms?: Partial<IssuerProgram>[];
};

import { PartyIssuerTab } from "./tabs/PartyIssuerTab";
import { PartyDocsTab } from "./tabs/PartyDocsTab";
import { ResponsiveTabsList } from "../../../../components/src/ResponsiveTabsList";

export interface PartiesParamTypes {
    id: string;
    tabId?: string;
    itemId?: string;
}

const availableTabs = (types?: readonly PartyType[]): Record<string, string> => {
    const tabs: Record<string, string> = {
        base: "base"
    };

    if (types && types.includes(PartyType.Fund)) {
        tabs.fundInfo = "fundInfo";
    }

    if (types && types.includes(PartyType.Issuer)) {
        tabs.issuer = "issuer";
    }

    if (types && types.includes(PartyType.Client)) {
        tabs.accounts = "accounts";
        tabs.externalAccounts = "externalAccounts";
        tabs.instruments = "instruments";
    }

    tabs.docs = "docs";

    return tabs;
};

export const PartyPage = (): ReactElement => {
    const { id: partyId, tabId } = useParams<{ id: string; tabId: string }>();
    const [, navigate] = useLocation();

    const [{ fetching: loading, error, data }, refetch] = useQuery<PartyPageData>({
        query: GET_PARTY,
        variables: { _id: partyId === "new" ? "000000000000000000000000" : partyId }
    });

    const partyData: PartyPageData = useMemo(() => {
        if (partyId === "new") {
            return { party: { _id: "000000000000000000000000", name: "", longName: "", types: [], fundInfo: null } };
        }
        return data;
    }, [partyId, data]);

    useLayoutEffect(() => {
        if (data?.party) {
            const types = cloneDeep(data.party.types);
            const tabs = availableTabs(types);
            if (!(tabId in tabs)) {
                navigate("/parties/" + partyId + "/base", { replace: true });
            }
        }
    }, [tabId, data, navigate, partyId]);

    const onTabChange = useCallback(
        (tabId: string) => {
            const types = data && data.party ? cloneDeep(data.party.types) : null;
            const tabs = availableTabs(types);
            if (!(tabId in tabs)) {
                navigate("/parties/" + partyId + "/base", { replace: true });
            } else {
                navigate("/parties/" + partyId + "/" + tabId, { replace: true });
            }
        },
        [partyId, data, navigate]
    );

    if (error)
        return (
            <div>
                <p>Error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    if (loading)
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );

    const types = data && data.party ? cloneDeep(data.party.types) : null;
    const tabs = availableTabs(types);

    if (!partyData.party) return <div>No party found with that id</div>;

    return (
        <div>
            <div>
                <h3>{partyData.party.name}</h3>
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className="bg-white rounded-lg shadow p-4 bg-gray-100 w-full px-2 mb-4">
                    <Tabs.Root value={tabId || "base"} onValueChange={onTabChange} className="w-full">
                        <ResponsiveTabsList value={tabId || "base"} onValueChange={onTabChange}>
                            <Tabs.Trigger
                                value="base"
                                className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                            >
                                Base
                            </Tabs.Trigger>
                            {partyData.party.fundInfo ? (
                                <Tabs.Trigger
                                    value="fundInfo"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Fund info
                                </Tabs.Trigger>
                            ) : null}
                            {"accounts" in tabs ? (
                                <Tabs.Trigger
                                    value="accounts"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Accounts
                                </Tabs.Trigger>
                            ) : null}
                            {"externalAccounts" in tabs ? (
                                <Tabs.Trigger
                                    value="externalAccounts"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    External accounts
                                </Tabs.Trigger>
                            ) : null}
                            {"instruments" in tabs ? (
                                <Tabs.Trigger
                                    value="instruments"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Instruments
                                </Tabs.Trigger>
                            ) : null}
                            {"issuer" in tabs ? (
                                <Tabs.Trigger
                                    value="issuer"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Issuer
                                </Tabs.Trigger>
                            ) : null}
                            {"docs" in tabs && partyId !== "new" ? (
                                <Tabs.Trigger
                                    value="docs"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    Docs
                                </Tabs.Trigger>
                            ) : null}
                        </ResponsiveTabsList>

                        <Tabs.Content value="base" className="mt-4">
                            <PartyBaseTab key={`/parties/${partyId}/base`} data={partyData} refetch={refetch} />
                        </Tabs.Content>
                        {partyData.party.fundInfo ? (
                            <Tabs.Content value="fundInfo" className="mt-4">
                                <PartyFundInfoTab key={`/parties/${partyId}/fundInfo`} data={partyData} refetch={refetch} />
                            </Tabs.Content>
                        ) : null}
                        {"accounts" in tabs ? (
                            <Tabs.Content value="accounts" className="mt-4">
                                {tabId === "accounts" ? (
                                    <PartyAccountsTab key={`/parties/${partyId}/accounts`} data={partyData} refetch={refetch} />
                                ) : null}
                            </Tabs.Content>
                        ) : null}
                        {"externalAccounts" in tabs ? (
                            <Tabs.Content value="externalAccounts" className="mt-4">
                                {tabId == "externalAccounts" ? (
                                    <PartyExternalAccountsTab
                                        key={`/parties/${partyId}/externalAccounts`}
                                        data={partyData}
                                        refetch={refetch}
                                    />
                                ) : null}
                            </Tabs.Content>
                        ) : null}
                        {"instruments" in tabs ? (
                            <Tabs.Content value="instruments" className="mt-4">
                                <PartyInstrumentsTab key={`/parties/${partyId}/instruments`} data={partyData} refetch={refetch} />
                            </Tabs.Content>
                        ) : null}
                        {"issuer" in tabs ? (
                            <Tabs.Content value="issuer" className="mt-4">
                                <PartyIssuerTab key={`/parties/${partyId}/issuer`} data={partyData} refetch={refetch} />
                            </Tabs.Content>
                        ) : null}
                        {"docs" in tabs && partyId !== "new" ? (
                            <Tabs.Content value="docs" className="mt-4">
                                <PartyDocsTab key={`/parties/${partyId}/docs`} />
                            </Tabs.Content>
                        ) : null}
                    </Tabs.Root>
                </div>
            </div>
        </div>
    );
};
